import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BLANK = {
  name: "", contact_person: "", phone: "", email: "", address: "",
  gstin: "", agreement_start: "", royalty_percent: 0, notes: "", active: true,
};

export default function Franchisees() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/franchisees").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) return toast.error("Name required");
    try {
      if (editing.id) {
        const { id, created_at, ...payload } = editing;
        await api.patch(`/franchisees/${id}`, payload);
      } else {
        await api.post("/franchisees", editing);
      }
      setEditing(null);
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this franchisee?")) return;
    try {
      await api.delete(`/franchisees/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Franchisees</h1>
          <p className="text-sm text-boba-inkSoft mt-1">Franchise partners operating Hizli outlets</p>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="h-11 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2" data-testid="add-franchisee-btn">
          <Plus size={18} weight="bold" /> Add Franchisee
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6">
        <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-boba-muted text-xs uppercase tracking-widest text-boba-inkSoft font-mono">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">GSTIN</th>
                <th className="text-right px-4 py-3">Royalty</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="franchisees-table">
              {items.map((f) => (
                <tr key={f.id} className="border-t border-boba-stoneLine">
                  <td className="px-4 py-3 font-semibold">{f.name}</td>
                  <td className="px-4 py-3">{f.contact_person || "—"}</td>
                  <td className="px-4 py-3 font-mono text-boba-inkSoft">{f.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono text-boba-inkSoft">{f.gstin || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{f.royalty_percent}%</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${f.active ? "bg-boba-matcha/10 text-boba-matcha" : "bg-stone-100 text-stone-500"}`}>
                      {f.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(f)} className="p-2 hover:bg-stone-100 rounded mr-1" data-testid={`edit-franchisee-${f.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => del(f.id)} className="p-2 hover:bg-red-50 text-boba-danger rounded">
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-boba-inkSoft">No franchisees yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="franchisee-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Edit Franchisee" : "New Franchisee"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <Field label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="fr-name" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact person" value={editing.contact_person} onChange={(v) => setEditing({ ...editing, contact_person: v })} testId="fr-contact" />
                <Field label="Phone" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testId="fr-phone" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testId="fr-email" />
                <Field label="GSTIN" value={editing.gstin} onChange={(v) => setEditing({ ...editing, gstin: v })} testId="fr-gstin" />
              </div>
              <Field label="Address" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} testId="fr-address" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Agreement start" type="date" value={editing.agreement_start} onChange={(v) => setEditing({ ...editing, agreement_start: v })} testId="fr-agreement" />
                <Field label="Royalty %" type="number" step="0.1" value={editing.royalty_percent} onChange={(v) => setEditing({ ...editing, royalty_percent: parseFloat(v) || 0 })} testId="fr-royalty" />
              </div>
              <Field label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} testId="fr-notes" />
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} id="fr-active" />
                <label htmlFor="fr-active" className="text-sm">Active</label>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={save} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="fr-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">{label}</label>
      <input
        type={type}
        step={step}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md"
        data-testid={testId}
      />
    </div>
  );
}
